import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatAccordion } from '@angular/material/expansion';
import { MatExpansionPanel } from '@angular/material/expansion';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import { BRAND } from '../../constants/brand';

type IRoles = 'externo' | 'interno' | 'full';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  animations: [
    trigger('delayedDisplay', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('50ms 230ms ease-in', style({ opacity: 1 }))
      ])
    ])
  ]
})
export class MenuComponent implements OnInit {
  readonly brand = BRAND;
  @ViewChild(MatAccordion) accordion: MatAccordion | undefined;
  @ViewChildren(MatExpansionPanel)
  expansionPanels!: QueryList<MatExpansionPanel>;
  isMenuExpanded: boolean = false;
  isMobileMenuOpen: boolean = false;
  activeRoute: string = '';
  userOpenedPanel: string | null = null;
  panelSelectedState: { [key: string]: boolean } = {
    frotas: false,
    agendamento: false,
    parametrizacao: false,
    acessosInternos: false,
    edicaoEmpresa: false,
    gateEntrada: false,
    empresa: false
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    private eRef: ElementRef
  ) {}

  ngOnInit() {
    this.setInitialActiveRoute();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.setInitialActiveRoute();
      });
  }

  setInitialActiveRoute() {
    this.activeRoute = this.router.url;
    this.updateSelection();
  }

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent) {
    if (!this.isMenuExpanded) return;

    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeMenuByOutsideClick();
    }
  }

  closeMenuByOutsideClick() {
    const menuContainer = document.querySelector('.menu-container');

    menuContainer?.classList.remove('expanded');
    this.accordion?.closeAll();
    this.userOpenedPanel = null;
    this.toggleMobileMenu();
    this.isMenuExpanded = false;

    this.cd.detectChanges();
  }

  viewMenu(menu: string) {
    const userType = this.authService.getCurrentUserType();

    if (userType === 'FUNC') {
      const allowedFuncionarioMenus = [
        'frotas',
        'agendamento',
        'servicos',
        'laudosHigienizacao',
        'edicaoEmpresa',
        'listagemCarreta',
        'listagemMotorista',
        'listagemCavalo',
        'listagemAgendamento',
        'cadastroAgendamento',
        'listagemServicos',
        'cadastroServicos',
        'listagemLaudosHigienizacao',
        'cadastroLaudo',
        'listagemFuncionario',
        'cadastroFuncionario'
      ];

      return allowedFuncionarioMenus.includes(menu);
    }

    if (userType === 'PJ') {
      if (['listagemFuncionario', 'cadastroFuncionario'].includes(menu)) {
        return true;
      }
    }

    const nomeRole: IRoles | null = localStorage.getItem(
      'role'
    ) as IRoles | null;

    const tipoRole: Record<IRoles, number> = {
      externo: 0,
      interno: 1,
      full: 2
    };

    const tipoMenu: any = {
      frotas: 0,
      agendamento: 2,
      empresa: 1,
      parametrizacao: 1,
      acessosInternos: 1,
      gateEntrada: 1,
      servicos: 2,
      laudosHigienizacao: 2,
      engateDesengate: 1
    };

    const tipoMenuInterno: any = {
      listagemCarreta: 0,
      listagemMotorista: 0,
      listagemCavalo: 0,
      listagemPatio: 1,
      listagemEmpresa: 1,
      listagemServico: 1,
      listagemPrecificacao: 1,
      listagemAgendamento: 2,
      listagemParametrizacao: 1,
      listagemFuncionario: 1,
      listagemEntradas: 1,
      listagemServicos: 2,
      listagemLaudosHigienizacao: 2,
      listagemEngates: 1,
      listagemDesengates: 1,
      cadastroEntrada: 1,
      cadastroServicos: 1,
      cadastroAgendamento: 0,
      cadastroFuncionario: 1,
      cadastroLaudo: 1,
      edicaoEmpresa: 0
    };

    if (nomeRole !== null && tipoRole.hasOwnProperty(nomeRole)) {
      const role = tipoRole[nomeRole];
      if (tipoMenu[menu] === role || tipoMenu[menu] === tipoRole['full'])
        return true;

      if (
        tipoMenuInterno[menu] === role ||
        tipoMenuInterno[menu] == tipoRole['full']
      )
        return true;

      return false;
    }
    return false;
  }

  updateSelection() {
    const routes = [
      'frotas',
      'agendamento',
      'parametrizacao',
      'acessos-internos',
      'edicao-empresa',
      'gateEntrada',
      'empresa',
      'servicos',
      'laudosHigienizacao',
      'engateDesengate'
    ];
    routes.forEach(route => {
      this.panelSelectedState[route] = false;
    });

    if (this.userOpenedPanel) {
      this.panelSelectedState[this.userOpenedPanel] = true;
    }

    this.cd.detectChanges();
  }

  expand(isValue: boolean = false) {
    const menuContainer = document.querySelector('.menu-container');

    if (isValue) {
      if (!menuContainer?.classList.contains('expanded')) {
        menuContainer?.classList.add('expanded');
        this.toggleMobileMenu();
        this.isMenuExpanded = true;
      } else {
        menuContainer?.classList.remove('expanded');
        this.accordion?.closeAll();
        this.userOpenedPanel = null;
        this.toggleMobileMenu();
        this.isMenuExpanded = false;
      }
    } else {
      if (!menuContainer?.classList.contains('expanded')) {
        menuContainer?.classList.add('expanded');
        this.toggleMobileMenu();
        this.isMenuExpanded = true;
      }
    }
  }

  openAccordeon(accordionId: string) {
    this.userOpenedPanel = accordionId;
    this.updateSelection();
  }

  closeAccordeon(accordionId: string) {
    if (this.userOpenedPanel === accordionId) {
      this.userOpenedPanel = null;
    }
    this.closeAllPanels();
    this.updateSelection();
  }

  closeAllPanels() {
    this.expansionPanels.forEach(panel => panel.close());
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;

    if (!this.isMobileMenuOpen && this.accordion) {
      this.accordion.closeAll();
      this.userOpenedPanel = null;
    }
    if (!this.isMobileMenuOpen) {
      this.closeAllPanels();
    }
  }

  goTo(route: string) {
    const userType = this.authService.getCurrentUserType();
    const userRoute = userType === 'PF' ? 'motorista-externo/' : 'empresa/';
    if (route === 'home') {
      this.router.navigate([route]);
      return;
    }

    this.router.navigate([userRoute + route]);
    setTimeout(() => {
      this.activeRoute = this.router.url;
      this.cd.detectChanges();
    }, 200);
  }

  isFuncionario(): boolean {
    return this.authService.getCurrentUserType() === 'FUNC';
  }
}
